import type {editor, IDisposable} from 'monaco-editor';
import {useEffect, useState} from 'react';

import type {EditorType, RenderMode} from './types';

// Returns true if the editor is a diff editor, false if it's a regular code
// editor. This is a type guard that allows us to access diff-editor-specific
// methods in computeLineCount and onChange without TypeScript errors.
function isDiffEditor(
  editor: EditorType
): editor is editor.IStandaloneDiffEditor {
  return 'getOriginalEditor' in editor && 'getModifiedEditor' in editor;
}

// Returns the number of original lines removed by a single diff hunk.
// originalEndLineNumber <= 0 means a pure insertion (nothing deleted), so we
// return 0. Otherwise, end - start + 1 gives the inclusive range length. Only
// deletions need to be counted: in inline mode Monaco renders the modified file
// and interleaves deleted lines as extra rows, so total height =
// modifiedLineCount + deletedLineCount. Added lines are already in
// modifiedLineCount.
function calculateDeletedLines(change: editor.ILineChange): number {
  if (change.originalEndLineNumber <= 0) {
    return 0;
  }
  return change.originalEndLineNumber - change.originalStartLineNumber + 1;
}

// Given an editor and a render mode, compute the number of lines that are
// renderd in the diff editor. This is used to set the height of the editor
// container so that the entire diff is visible without scrolling. The logic
// differs based on whether we're in side-by-side or inline mode, whether
// the diff has finished computing yet (getLineChanges() returns null while
// the diff is still being computed), and whether we're using a diff editor at
// all!
function computeLineCount(editor: EditorType, renderMode: RenderMode): number {
  if (!isDiffEditor(editor)) {
    // If we are using a regular editor (in the case that we cannot find the
    // file to apply the patch to), just return the line count for the editor
    // instance. This only works for IStandaloneCodeEditor, hence the type
    // guard above.
    return editor.getModel()?.getLineCount() ?? 0;
  }

  const lineChanges = editor.getLineChanges();
  const originalModel = editor.getOriginalEditor().getModel();
  const modifiedModel = editor.getModifiedEditor().getModel();
  const originalLineCount = originalModel?.getLineCount() ?? 0;
  const modifiedLineCount = modifiedModel?.getLineCount() ?? 0;

  if (!lineChanges || renderMode === 'side-by-side') {
    return Math.max(originalLineCount, modifiedLineCount);
  }

  const deletedLineCount = lineChanges.reduce((sum, change) => {
    return sum + calculateDeletedLines(change);
  }, 0);

  return modifiedLineCount + deletedLineCount;
}

// Wrapper fn to attach the appropriate change listener based on whether we're
// using a diff editor or a regular code editor. In a diff editor we want to
// listen for onDidUpdateDiff, which fires after the diff has finished
// computing. In a regular editor we want to listen for onDidChangeModelContent,
// which fires after any change to the editor content.
//
// The expectation here is that the editor content shouldn't change much, but
// there's no reason not to be extra-paranoid when it's so cheap to do so.
function onChange(editor: EditorType, callback: () => void): IDisposable {
  if (isDiffEditor(editor)) {
    return editor.onDidUpdateDiff(callback);
  }
  return editor.onDidChangeModelContent(callback);
}

/**
 * Returns the number of visible lines in a Monaco diff editor, updating
 * whenever Monaco finishes computing the diff.
 *
 * In inline mode the unified diff renders modified lines with deleted lines
 * interleaved, so the total is modifiedLineCount + deletedLineCount.
 * In side-by-side mode each pane shows its own content independently, so the
 * visible height is determined by whichever side is longer.
 * getLineChanges() returns null while the diff is still computing — we fall
 * back to modifiedLineCount alone until onDidUpdateDiff fires.
 */
export function usePatchLineCount(
  editorInstance: EditorType | null,
  renderMode: RenderMode
): number {
  const [lineCount, setLineCount] = useState(0);

  useEffect(() => {
    if (!editorInstance) {
      return;
    }

    setLineCount(computeLineCount(editorInstance, renderMode));
    const disposable = onChange(editorInstance, () => {
      setLineCount(computeLineCount(editorInstance, renderMode));
    });
    return () => disposable.dispose();
  }, [editorInstance, renderMode]);

  return lineCount;
}
